<template>
  <div class="cancel-sub-modal">
    <!-- Action Selection Page -->
    <template v-if="currentPage === 0">
      <div class="cancel-action-selection">
        <div v-for="action in config.actionSelection.actions" :key="action.id" class="cancel-action-card" :class="{
          selected: formData.selectedCancelAction === action.id,
          danger: action.severity === 'danger'
        }" @click="selectAction(action.id)" @keydown.enter="selectAction(action.id)"
          @keydown.space.prevent="selectAction(action.id)" role="button" tabindex="0">
          <div class="action-title">{{ action.title }}</div>
          <div class="action-description">{{ action.description }}</div>
          <div class="action-disclaimer" :class="action.severity">
            {{ action.disclaimer }}
          </div>
        </div>
      </div>
    </template>

    <!-- Flow Pages -->
    <template v-else>
      <ModalPageRenderer 
        v-if="currentFlowPage"
        :page="currentFlowPage" 
        :form-data="formData" 
        :is-loading="isLoading" 
        :tenant-name="tenantName"
        :subscription-name="subscriptionName" 
        :tenancy-subscription-data="tenancySubscriptionData"
        :subscription-plan="subscriptionPlan" 
        :orders="orders" 
        :tenancy-subscription="tenancySubscription"
        :effective-date-text="effectiveDateText"
        :invoice-status-text="invoiceStatusText"
        @form-update="$emit('form-update', $event)"
        @submit="handleSubmit" 
        @next-page="nextPage" 
        @back="goBack" 
        @cancel="$emit('modal-close')" />

      <!-- Fallback for when currentFlowPage is null -->
      <div v-else class="loading-state">
        <p>Laster...</p>
      </div>
    </template>
  </div>
</template>

<script>
import ModalPageRenderer from './ModalPageRenderer.vue';

export default {
  name: 'CancelSubscriptionModalRenderer',
  components: {
    ModalPageRenderer
  },
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    config: {
      type: Object,
      required: true
    },
    formData: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      default: '',
    },
    subscriptionName: {
      type: String,
      default: '',
    },
    tenancySubscriptionData: {
      type: Object,
      required: true
    },
    subscriptionPlan: {
      type: String,
      required: true
    },
    orders: {
      type: Array,
      default: () => []
    },
    tenancySubscription: {
      type: Object,
      default: () => null
    },
    effectiveDateText: {
      type: String,
      default: ''
    },
    invoiceStatusText: {
      type: String,
      default: ''
    }
  },
  emits: ['page-change', 'form-update', 'action-submit', 'modal-close'],
  computed: {
    currentFlowPage() {
      if (!this.formData.selectedCancelAction || this.currentPage === 0) return null;
      const flow = this.config.flows?.[this.formData.selectedCancelAction];
      if (!flow || !Array.isArray(flow)) return null;

      // For terminate_only flow, skip reason selection page if it's an upgrade
      if (this.formData.selectedCancelAction === 'terminate_only' && this.formData.isDowngradeFlow === false) {
        if (this.currentPage === 1) {
          return flow[0]; // subscription selection
        } else if (this.currentPage === 2) {
          return flow[2]; // confirmation (skip reason if isUpgrade)
        } else if (this.currentPage === 3) {
          return flow[3]; // success
        }
      }

      return flow ? flow[this.currentPage - 1] : null;
    },
  },
  methods: {
    selectAction(actionId) {
      this.$emit('form-update', { selectedCancelAction: actionId });
      this.$emit('page-change', this.currentPage + 1);
    },
    nextPage() {
      this.$emit('page-change', this.currentPage + 1);
    },
    goBack() {
      this.$emit('page-change', this.currentPage - 1);
    },
    handleSubmit(actionMethod) {
      this.$emit('action-submit', actionMethod);
    }
  }
}
</script>

<style scoped lang="scss">
// Action selection cards styling
.cancel-action-selection {
  display: flex;
  flex-direction: column;
  text-align: left;
  gap: 16px;
  margin: 20px 0;
}

.cancel-action-card {
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
    background-color: #f8fafc;
  }

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }


  &.selected {
    border-color: #3b82f6;
    background-color: #eff6ff;
  }

  &.danger:hover,
  &.danger.selected {
    border-color: #dc2626;
    background-color: #fef2f2;
  }

  .action-title {
    font-weight: 600;
    font-size: 16px;
    margin-bottom: 8px;
    color: #1f2937;
  }

  .action-description {
    font-size: 14px;
    color: #6b7280;
    margin-bottom: 12px;
    line-height: 1.5;
  }

  .action-disclaimer {
    font-size: 13px;
    padding: 8px 12px;
    border-radius: 4px;
    background-color: #3b82f6;

    &.warning {
      background-color: #fef3cd;
      color: #92400e;
    }

    &.danger {
      background-color: #fee2e2;
      color: #dc2626;
    }
  }
}

.cancel-sub-modal {
  position: relative;
}
</style>
